import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMemberFlats,
    getMemberLedger,
} from '../../../redux/actions/SocietyPortal/MemberPortalAction/MemberPortalAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
// The member statement is the Account Ledger Report pointed at one flat, so it
// reuses that report's financial-year list and number formatting rather than
// growing a second, subtly different copy of the Apr-March rule.
import { FINANCIAL_YEARS, fmt } from '../Reports/AccountLedgerReport/utils/helpers';
import { fmtCurrency, flatLabel } from './memberConstants';

// The backend answers with the same envelope as the Account Ledger Report:
// reportInfo, openingEntries, and entries carrying drAmount / crAmount /
// balance. Only the header and the wording differ — this screen is read by a
// resident, not an accountant, so amounts stay plain rather than Dr/Cr-suffixed.
const HEADERS = ['Date', 'Particulars', 'Debit', 'Credit', 'Balance'];
const isAmountCol = (h) => h === 'Debit' || h === 'Credit' || h === 'Balance';

// "2026-2027" (the shared list's key) -> "2026-27" (what the endpoint takes).
const shortFY = (value) => {
    const [start, end] = String(value || '').split('-');
    return end ? `${start}-${end.slice(-2)}` : value;
};

const Amount = ({ value, tone }) => {
    if (!value) return <span className="tw-text-gray-300">—</span>;
    return <span className={`tw-font-medium ${tone}`}>{fmt(value)}</span>;
};

// A charge or a payment. `narration` is the accountant's line; the document
// number under it is what the member recognises from their own paperwork.
const EntryRow = ({ entry, dense }) => (
    <tr className={`tw-border-b tw-border-gray-50 hover:tw-bg-slate-50/60 ${dense ? 'tw-bg-white' : ''}`}>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2.5'} tw-text-xs tw-text-gray-600 tw-whitespace-nowrap`}>
            {entry.date || '—'}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2.5'} tw-text-xs tw-text-gray-700`}>
            {entry.narration || entry.particulars || '—'}
            {(entry.documentNumber || entry.journalNo) && (
                <span className="tw-block tw-text-[11px] tw-font-mono tw-text-gray-400">
                    {entry.documentNumber || entry.journalNo}
                </span>
            )}
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2.5'} tw-text-xs tw-text-right tw-tabular-nums tw-whitespace-nowrap`}>
            <Amount value={entry.drAmount} tone="tw-text-rose-600" />
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2.5'} tw-text-xs tw-text-right tw-tabular-nums tw-whitespace-nowrap`}>
            <Amount value={entry.crAmount} tone="tw-text-emerald-600" />
        </td>
        <td className={`tw-px-3 ${dense ? 'tw-py-1.5' : 'tw-py-2.5'} tw-text-xs tw-font-semibold tw-text-right tw-tabular-nums tw-text-gray-800 tw-whitespace-nowrap`}>
            {fmt(entry.balance)}
        </td>
    </tr>
);

const Figure = ({ label, value, emphasis }) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
        <span className="tw-text-[10px] tw-uppercase tw-tracking-wide tw-text-gray-500 tw-font-semibold">{label}</span>
        <span className={`tw-text-base tw-font-bold tw-tabular-nums ${emphasis || 'tw-text-gray-800'}`}>{value}</span>
    </div>
);

// Statement of account for one flat over one window.
//
// Two things make this different from the bills list. It is never paginated — a
// running balance only means something if the whole window comes back at once.
// And it opens with the balance brought forward, which folds in the flat's
// migrated opening balance plus every transaction before the window; without it
// a date-filtered statement would start from zero mid-history and every balance
// below it would be wrong. When the backend sends openingEntries, that row
// expands to show exactly what went into it.
const MyLedger = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { ledgerData, ledgerLoading, flatsData } =
        useSelector((state) => state.MemberPortalReducer || {});

    const [financialYear, setFinancialYear] = useState(FINANCIAL_YEARS[0]?.value);
    const [flatId, setFlatId] = useState('');
    const [openingExpanded, setOpeningExpanded] = useState(false);

    const flats = useMemo(() => (Array.isArray(flatsData) ? flatsData : []), [flatsData]);

    useEffect(() => {
        if (societyId) dispatch(getMemberFlats({ societyId }));
    }, [dispatch, societyId]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        // The shared list keys years as "2026-2027"; /api/member/ledger takes the
        // short "2026-27" form. The window is named one way only — the endpoint
        // rejects a financial_year sent alongside an explicit from/to range.
        dispatch(getMemberLedger({
            societyId,
            financialYear: shortFY(financialYear),
            flatId: flatId || undefined,
        }));
    }, [dispatch, societyId, financialYear, flatId, refreshKey]);

    // Collapse the drill-down whenever a different statement loads.
    useEffect(() => { setOpeningExpanded(false); }, [financialYear, flatId]);

    const info = ledgerData?.reportInfo;
    const entries = useMemo(() => ledgerData?.entries || [], [ledgerData]);
    const openingEntries = useMemo(() => ledgerData?.openingEntries || [], [ledgerData]);
    const openingBalance = ledgerData?.openingBalance ?? 0;
    const totalDebit = ledgerData?.totalDebit ?? 0;
    const totalCredit = ledgerData?.totalCredit ?? 0;
    const closingBalance = ledgerData?.closingBalance ?? 0;

    const hasOpeningDetail = openingEntries.length > 0;
    const isEmpty = entries.length === 0 && !hasOpeningDetail && !openingBalance;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div>
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800 tw-flex tw-items-center tw-gap-2">
                            <Icon icon="hugeicons:notebook-01" className="tw-text-sky-600" />
                            Statement of account
                        </h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Every charge and payment on your flat, in order, with a running balance
                        </p>
                    </div>
                    <button
                        onClick={() => window.print()}
                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 hover:tw-border-sky-400 hover:tw-text-sky-700"
                    >
                        <Icon icon="mdi:printer-outline" /> Print / save as PDF
                    </button>
                </div>

                <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-bg-gray-50/50 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                    <select
                        value={financialYear}
                        onChange={(e) => setFinancialYear(e.target.value)}
                        className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                    >
                        {FINANCIAL_YEARS.map((y) => (
                            <option key={y.value} value={y.value}>{y.label}</option>
                        ))}
                    </select>
                    {flats.length > 1 && (
                        <select
                            value={flatId}
                            onChange={(e) => setFlatId(e.target.value)}
                            className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-sky-500"
                        >
                            <option value="">All my flats</option>
                            {flats.map((f) => (
                                <option key={f.flat_id} value={f.flat_id}>{flatLabel(f)}</option>
                            ))}
                        </select>
                    )}
                </div>

                {ledgerLoading && (
                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                        <span className="tw-text-xs tw-text-gray-400">Loading...</span>
                    </div>
                )}

                {!ledgerLoading && !ledgerData && (
                    <div className="tw-text-center tw-py-16">
                        <Icon icon="hugeicons:notebook-01" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No statement available for this period</p>
                    </div>
                )}

                {!ledgerLoading && ledgerData && (
                    <>
                        {/* Report header — the letterhead a member would expect on a
                            statement they print and take to the society office. */}
                        <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-text-center">
                            <p className="tw-text-sm tw-font-bold tw-text-gray-800">{info?.societyName || '—'}</p>
                            {info?.societyAddress && (
                                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{info.societyAddress}</p>
                            )}
                            <p className="tw-inline-block tw-mt-2.5 tw-pt-2 tw-px-6 tw-border-t tw-border-gray-100 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-sky-700">
                                Member Ledger Report
                            </p>
                            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-5 tw-gap-y-1 tw-mt-1 tw-text-[11px] tw-text-gray-600">
                                {info?.memberName && <span>{info.memberName}</span>}
                                {info?.flatNumber && <span>Flat {info.flatNumber}</span>}
                                {(info?.fromDate || info?.toDate) && (
                                    <span>{info.fromDate} &ndash; {info.toDate}</span>
                                )}
                            </div>
                        </div>

                        <div className="tw-flex tw-flex-wrap tw-gap-6 tw-p-4 tw-border-b tw-border-gray-100">
                            <Figure label="Opening balance" value={fmtCurrency(openingBalance)} />
                            <Figure label="Charged" value={fmtCurrency(totalDebit)} />
                            <Figure label="Paid" value={fmtCurrency(totalCredit)} />
                            <Figure
                                label="Closing balance"
                                value={fmtCurrency(closingBalance)}
                                emphasis={Number(closingBalance) > 0 ? 'tw-text-rose-600' : 'tw-text-emerald-600'}
                            />
                        </div>

                        <div className="tw-p-4 tw-overflow-x-auto">
                            <table className="tw-w-full tw-min-w-[560px]">
                                <thead>
                                    <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                                        {HEADERS.map((h) => (
                                            <th
                                                key={h}
                                                className={`tw-px-3 tw-py-2.5 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${isAmountCol(h) ? 'tw-text-right' : 'tw-text-left'}`}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Balance brought forward — the row that makes every figure below it true */}
                                    {!isEmpty && (
                                        <tr
                                            className={`tw-border-b tw-border-gray-100 tw-bg-amber-50/60 ${hasOpeningDetail ? 'hover:tw-bg-amber-100/60 tw-cursor-pointer' : ''}`}
                                            onClick={() => hasOpeningDetail && setOpeningExpanded((s) => !s)}
                                        >
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">
                                                {info?.openingToDate || info?.fromDate || '—'}
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-amber-700">
                                                <span className="tw-inline-flex tw-items-center tw-gap-1.5">
                                                    {hasOpeningDetail && (
                                                        <Icon
                                                            icon={openingExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                                                            className="tw-text-base"
                                                        />
                                                    )}
                                                    Opening balance brought forward
                                                </span>
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-right tw-text-gray-300 tw-text-xs">—</td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-right tw-text-gray-300 tw-text-xs">—</td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-text-right tw-tabular-nums tw-text-amber-700 tw-whitespace-nowrap">
                                                {fmt(openingBalance)}
                                            </td>
                                        </tr>
                                    )}

                                    {hasOpeningDetail && openingExpanded && (
                                        <tr>
                                            <td colSpan={HEADERS.length} className="tw-p-0 tw-bg-amber-50/30">
                                                <div className="tw-px-3 tw-py-2 tw-border-y tw-border-amber-100">
                                                    <p className="tw-text-[11px] tw-font-semibold tw-text-amber-800 tw-mb-1">
                                                        What makes up this balance
                                                        {info?.openingFromDate && info?.openingToDate && (
                                                            <span className="tw-font-normal tw-text-amber-700">
                                                                {' '}— {info.openingFromDate} to {info.openingToDate}
                                                            </span>
                                                        )}
                                                    </p>
                                                    <table className="tw-w-full">
                                                        <tbody>
                                                            {openingEntries.map((e, i) => (
                                                                <EntryRow key={`oe-${i}`} entry={e} dense />
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {entries.map((e, i) => (
                                        <EntryRow key={`e-${i}`} entry={e} />
                                    ))}

                                    {isEmpty && (
                                        <tr>
                                            <td colSpan={HEADERS.length} className="tw-text-center tw-py-16">
                                                <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
                                                    No transactions in this period
                                                </p>
                                            </td>
                                        </tr>
                                    )}

                                    {!isEmpty && (
                                        <tr className="tw-bg-gray-50 tw-border-t-2 tw-border-gray-800">
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-text-gray-700 tw-whitespace-nowrap">
                                                {info?.toDate || '—'}
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-gray-700">Closing balance</td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-tabular-nums tw-text-rose-600 tw-whitespace-nowrap">
                                                {fmt(totalDebit)}
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-tabular-nums tw-text-emerald-600 tw-whitespace-nowrap">
                                                {fmt(totalCredit)}
                                            </td>
                                            <td className="tw-px-3 tw-py-2.5 tw-text-xs tw-font-bold tw-text-right tw-tabular-nums tw-text-gray-800 tw-whitespace-nowrap">
                                                {fmt(closingBalance)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MyLedger;
